import * as React from 'react';
import { InputControlContext } from '../common/input-control-context';
import DynamicNumber from 'react-dynamic-number';
import debounce from 'lodash/debounce';
import styles from './numberbox.scss';
import { SingleValueComponent } from 'ui/common/types';
import { ValidationLevel } from 'ui/common/validation/types';
import { Validation } from 'ui/common/validation';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { SmallNumberbox } from 'ui/numberbox/small-numberbox';
import { isFloat, isNumberExponential, toFixed } from 'ui/numberbox/utils';
import { replaceAll } from 'app/infrastructure/data/string';

/**
 * Describes how the Number box component is displayed.
 *
 */
export interface NumberboxProps extends SingleValueComponent<number> {
  /**
   * Determine locale used. Default is fallback to current browser settings. Default is English.
   */
  locale: string;
  /**
   * Translation text for invalid range error.
   */
  textInvalidRangeError: string;
  /**
   * Set the minimum value for the number input. Default is 0.
   */
  minValue?: number;
  /**
   * Set the maximum value for the number input. Default is 9007199254740991(MAX_SAFE_INTEGER).
   */
  maxValue?: number;
  /**
   * Set maximum number of digits in decimals part. Default is 0.
   */
  maxDecimals?: number;
  /**
   * Set maximum number of digits in integer part. Default is undefined.
   */
  maxIntegerDigits?: number;
  /**
   * Define if thousand separator should be enabled. Default is true.
   * When ' ' (space) - thousand separator is enabled and separate values by space.
   * When true, thousand separator is enabled and separate values depending on the locale being used.
   * -If decimal separator supported by the locale is dot then thousand separator is comma.
   * -If decimal separator supported by the locale is comma then thousand separator is dot.
   */
  useThousand?: boolean | ' ';
  /**
   * Debounce time. Default is 100.
   */
  wait?: number;
}

export interface NumberboxState {
  valueIsOutOfRange: boolean;
  prevValue: number;
  hasFocus?: boolean;
}

export class Numberbox extends React.Component<NumberboxProps, NumberboxState> {
  private debounceOnChange: any;
  public static defaultProps: Partial<NumberboxProps> = {
    maxValue: Number.MAX_SAFE_INTEGER,
    minValue: 0,
    maxDecimals: 0
  };
  private decimalSeparator: string;
  private thousandSeparator: string;
  private isPositive: boolean;
  private isNegative: boolean;
  private numericMaxValue: number;
  private numericMinValue: number;
  private allowUpdates: boolean;
  public static MAX_SAFE_SMALL_NUMBER: number = 0.000001;

  public static getDerivedStateFromProps(props: NumberboxProps, state: NumberboxState) {
    if (!state.hasFocus && props.value !== state.prevValue) {
      return {
        prevValue: props.value
      };
    }

    return {};
  }

  constructor(props: NumberboxProps) {
    super(props);

    const { wait = 100 } = this.props;

    this.state = {
      valueIsOutOfRange: false,
      prevValue: props.value,
      hasFocus: false
    };
    this.debounceOnChange = debounce((value: number) => {
      this.allowUpdates = true;
      this.props.onChange(value);
    }, wait);

    this.allowUpdates = true;

    this.decimalSeparator = (new Intl.NumberFormat(this.props.locale)).format(0.1).split('')[1];
    this.thousandSeparator = !this.props.useThousand ? this.decimalSeparator === '.' ? ',' : '.'
      : this.props.useThousand === ' ' ? ' ' : '';
    this.isPositive = this.props.maxValue > 0; //is positive until maxValue > 0
    this.isNegative = this.props.minValue < 0;  // is negative until minValue < 0
    const isMaxValueExponential = isNumberExponential(String(this.props.maxValue));
    const isMinValueExponential = isNumberExponential(String(this.props.minValue));
    this.numericMaxValue = !isMaxValueExponential ? Number(this.props.maxValue) : Number.MAX_SAFE_INTEGER;
    this.numericMinValue = !isMinValueExponential ? Number(this.props.minValue) : Number.MIN_SAFE_INTEGER;
  }

  /**
   * Handles if there is any input change.
   *
   */
  private handleOnChange = (evt: React.SyntheticEvent<HTMLInputElement>, modelValue: number) => {
    const inputValue: string = evt.currentTarget.value;

    if (inputValue === '-' && !this.isNegative) {
      this.setState({
        prevValue: undefined,
        valueIsOutOfRange: false
      });
      this.forceUpdate();
      return;
    }

    const lastCharacter = inputValue.slice(-1);
    const valueIsEmpty = !String(modelValue).length || !inputValue.length;
    const decimalPartAfterSeparator = inputValue?.split(this.decimalSeparator)[1];
    const decimalInput = decimalPartAfterSeparator && replaceAll(decimalPartAfterSeparator, this.thousandSeparator, '');
    const numOfDecimals = decimalInput?.length;
    const exceedsMaxDecimals = numOfDecimals && numOfDecimals > this.props.maxDecimals;
    const exceedsMaxValue = modelValue > this.numericMaxValue;
    const exceedsMinValue = modelValue < this.numericMinValue;
    const valueIsOutOfRange = exceedsMaxValue || exceedsMinValue;
    const isTrailingZero = decimalInput?.slice(-1) === '0';
    this.allowUpdates = false;

    if (exceedsMaxDecimals || exceedsMaxValue || exceedsMinValue) {
      evt.stopPropagation();
      if (valueIsOutOfRange) {
        this.setState({ valueIsOutOfRange: true });
        this.forceUpdate();
      }
      return;
    }

    //NB: modelValue is empty for string inputs and 0 for empty inputs by design
    if (valueIsEmpty) {
      this.debounceOnChange(undefined);
      this.setState({
        prevValue: undefined,
        valueIsOutOfRange: false
      });
      return;
    }

    if (isNaN(Number(lastCharacter)) || isTrailingZero) {
      return;
    }

    this.debounceOnChange(modelValue);
    this.setState({ valueIsOutOfRange: false, prevValue: modelValue });
  };

  private handleOnBlur = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    this.allowUpdates = true;
    const currentTarget: string = evt.currentTarget.value;
    const unformattedNumberValue: number = currentTarget &&
      Number(currentTarget.split(this.thousandSeparator).join('').replace(this.decimalSeparator, '.'));

    if (currentTarget && (unformattedNumberValue < this.props.minValue || unformattedNumberValue > this.props.maxValue)) {
      this.setState({ valueIsOutOfRange: true });
      return;
    }
    this.setState({ valueIsOutOfRange: false, hasFocus: false });
  };

  private onFocus = () => {
    this.setState({ hasFocus: true });
  };

  public shouldComponentUpdate() {
    return this.allowUpdates;
  }

  // eslint:disable-next-line: max-func-body-length
  public render() {
    const {
      componentId,
      className,
      label,
      info,
      infoLong,
      readOnly,
      value,
      placeholder,
      validation,
      maxDecimals,
      useThousand = true,
      textInvalidRangeError,
      maxIntegerDigits,
      locale,
      showAIChatIcon = false
    } = this.props;

    const { valueIsOutOfRange, prevValue } = this.state;
    const formattedNumericMinValue = Intl.NumberFormat(locale).format(this.numericMinValue);
    const formattedNumericMaxValue = Intl.NumberFormat(locale).format(this.numericMaxValue);
    const storeValueOrDefault = !isNaN(value) ? value : '';
    const prevValueorStoreValue = !isNaN(this.state.prevValue) ? this.state.prevValue : storeValueOrDefault;
    const shouldRoundToMaxDecimals = prevValueorStoreValue !== '' && isFloat(prevValueorStoreValue);

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed
    // 2.55.toFixed(1) returns '2.5'. Note that it rounds down. Due to unexpected results we are using
    // a custom, improved implementation of a function which formats and rounds a number to maximum decimal digits.
    const finalValue = shouldRoundToMaxDecimals ?
      toFixed(Number(prevValueorStoreValue), maxDecimals) : prevValueorStoreValue;
    
    const maxIntegerCount = maxIntegerDigits || (isDefinedIncludingZero(prevValue) && prevValue < 0
      ? String(Math.trunc(this.numericMinValue)).length
      : String(Math.trunc(this.numericMaxValue)).length);

    // SmallNumberbox component is used only for the value between (-0.000001, 0) U (0, 0.000001) range
    const valueIsTooSmall = Number(value) !== 0 && Number(value) > Number(-Numberbox.MAX_SAFE_SMALL_NUMBER) &&
      Number(value) < Number(Numberbox.MAX_SAFE_SMALL_NUMBER);

    const smallNumber =
      <SmallNumberbox
        className={`form-control ${styles.numberbox}`}
        value={finalValue}
        readOnly={readOnly}
        onChange={this.handleOnChange}
        integer={maxIntegerCount}
        fraction={maxDecimals}
        positive={this.isPositive}
        negative={this.isNegative}
        separator={this.decimalSeparator}
        disabled={readOnly}
        onBlur={this.handleOnBlur}
      />;
    const thousandSeparatorFromLocale = Intl.NumberFormat(this.props.locale)
      .formatToParts(10000)
      .find(part => part.type === 'group')?.value;
    // thousand separator from locale is &nbsp but the dynamic number component requires a space string.
    const nonBreakingSpace = String.fromCharCode(160);
    const dynamicNumberThousand: boolean | ' ' = !!useThousand && thousandSeparatorFromLocale === nonBreakingSpace ? ' ' : useThousand;

    const dynamicNumber =
      <DynamicNumber
        className={`form-control ${styles.numberbox}`}
        value={finalValue} //If value is '' then input is empty. Default is 0.
        readOnly={readOnly}
        placeholder={!readOnly ? placeholder : undefined}
        onChange={this.handleOnChange}
        integer={maxIntegerCount}
        fraction={maxDecimals}
        positive={this.isPositive}
        negative={this.isNegative}
        thousand={dynamicNumberThousand}
        separator={this.decimalSeparator}
        disabled={readOnly}
        onBlur={this.handleOnBlur}
        onFocus={this.onFocus}
      />;

    const errorParams = {
      level: ValidationLevel.error,
      messages: [{
        level: ValidationLevel.error,
        message: `${textInvalidRangeError} ${' ['} ${formattedNumericMinValue} ${'-'} ${formattedNumericMaxValue} ${']'}`
      }]
    };

    return (
      <InputControlContext
        componentId={componentId}
        label={label}
        info={info}
        infoLong={infoLong}
        validation={validation}
        readOnly={readOnly}
        className={className}
        showAIChatIcon={showAIChatIcon}
      >
        <div className="input-wrapper">
          {valueIsTooSmall ?
            smallNumber
            :
            dynamicNumber
          }
          {valueIsOutOfRange && <Validation {...errorParams} />}
        </div>
      </InputControlContext>
    );
  }
}
